import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Cancel, Edit, Save } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import ChapterSelector from './ChapterSelector';
import type {
  ChairRoleDTO,
  FundLabelOption,
  FundRoleSlotDTO,
  CreateFundRoleSlotRequest,
  PatchFundRoleSlotRequest,
} from '../types/financeFunds';
import type { SPDChapterListResponse } from '../types/chapters';

type ChapterKind = 'lc' | 'spd';

interface FundRoleSlotDisplayProps {
  title?: string;
  slots: FundRoleSlotDTO[];
  groupRoleOptions: ChairRoleDTO[];
  refRoleOptions: FundLabelOption[];
  spdChapters: SPDChapterListResponse[];
  canEdit: boolean;
  isSaving: boolean;
  fixedGroupRoleId?: string;
  emptyMessage?: string;
  onCreate: (body: CreateFundRoleSlotRequest) => Promise<void>;
  onPatch: (slotId: string, body: PatchFundRoleSlotRequest) => Promise<void>;
  onDelete: (slotId: string) => void;
}

const formatDate = (v?: string | null) =>
  v ? dayjs(v).format('MM/DD/YYYY') : '—';

const spdLabel = (sp: SPDChapterListResponse) => {
  const name = sp.name?.trim() || '';
  const abbr = sp.abbreviation?.trim() || '';
  if (name && abbr) return `${name} (${abbr})`;
  return name || abbr || sp.id;
};

function SlotEditor({
  slot,
  groupRoleOptions,
  refRoleOptions,
  spdChapters,
  fixedGroupRoleId,
  isSaving,
  onSave,
  onCancel,
}: {
  slot?: FundRoleSlotDTO;
  groupRoleOptions: ChairRoleDTO[];
  refRoleOptions: FundLabelOption[];
  spdChapters: SPDChapterListResponse[];
  fixedGroupRoleId?: string;
  isSaving: boolean;
  onSave: (
    body: CreateFundRoleSlotRequest | PatchFundRoleSlotRequest
  ) => Promise<void>;
  onCancel: () => void;
}) {
  const initialKind: ChapterKind = slot?.local_chapter_id ? 'lc' : 'spd';
  const [groupRoleId, setGroupRoleId] = useState(
    slot?.group_role_id || fixedGroupRoleId || groupRoleOptions[0]?.id || ''
  );
  const [refRoleId, setRefRoleId] = useState(slot?.ref_group_role_id || '');
  const [chapterKind, setChapterKind] = useState<ChapterKind>(initialKind);
  const [localChapterId, setLocalChapterId] = useState(
    slot?.local_chapter_id || ''
  );
  const [spdChapterId, setSpdChapterId] = useState(slot?.spd_chapter_id || '');
  const [startDate, setStartDate] = useState<Dayjs | null>(
    slot?.start_date ? dayjs(slot.start_date) : dayjs()
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    slot?.end_date ? dayjs(slot.end_date) : null
  );

  const handleSave = async () => {
    if (!refRoleId || !startDate) return;
    const roleId = fixedGroupRoleId || groupRoleId;
    if (!roleId) return;
    const start = startDate.format('YYYY-MM-DD');
    const end = endDate ? endDate.format('YYYY-MM-DD') : null;
    if (slot) {
      await onSave({
        group_role_id: roleId,
        ref_group_role_id: refRoleId,
        local_chapter_id: chapterKind === 'lc' ? localChapterId || null : null,
        spd_chapter_id: chapterKind === 'spd' ? spdChapterId || null : null,
        start_date: start,
        end_date: end,
      });
    } else {
      await onSave({
        group_role_id: roleId,
        ref_group_role_id: refRoleId,
        local_chapter_id:
          chapterKind === 'lc' ? localChapterId || undefined : undefined,
        spd_chapter_id:
          chapterKind === 'spd' ? spdChapterId || undefined : undefined,
        start_date: start,
        end_date: end || undefined,
      });
    }
  };

  const chapterValid = chapterKind === 'lc' ? !!localChapterId : !!spdChapterId;

  return (
    <Box
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}
    >
      <Stack spacing={2}>
        {!fixedGroupRoleId && groupRoleOptions.length > 0 && (
          <FormControl size="small" fullWidth>
            <InputLabel id="fund-role-slot-group-role">Fund role</InputLabel>
            <Select
              labelId="fund-role-slot-group-role"
              label="Fund role"
              value={groupRoleId}
              onChange={(e) => setGroupRoleId(String(e.target.value))}
            >
              {groupRoleOptions.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" fullWidth>
          <InputLabel id="fund-role-slot-ref-role">
            Chapter office role
          </InputLabel>
          <Select
            labelId="fund-role-slot-ref-role"
            label="Chapter office role"
            value={refRoleId}
            onChange={(e) => setRefRoleId(String(e.target.value))}
          >
            {refRoleOptions.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <RadioGroup
            row
            value={chapterKind}
            onChange={(e) => setChapterKind(e.target.value as ChapterKind)}
          >
            <FormControlLabel
              value="lc"
              control={<Radio size="small" />}
              label="Local chapter"
            />
            <FormControlLabel
              value="spd"
              control={<Radio size="small" />}
              label="State chapter"
            />
          </RadioGroup>
        </FormControl>
        {chapterKind === 'lc' ? (
          <ChapterSelector
            chapter={localChapterId}
            spd={spdChapterId || null}
            all={!spdChapterId}
            keyId={`role-slot-lc-${slot?.id || 'new'}`}
            label="Local chapter"
            handleChapterSelect={(v) => setLocalChapterId(v || '')}
          />
        ) : (
          <FormControl size="small" fullWidth>
            <InputLabel id="fund-role-slot-spd">State chapter</InputLabel>
            <Select
              labelId="fund-role-slot-spd"
              label="State chapter"
              value={spdChapterId}
              onChange={(e) => setSpdChapterId(String(e.target.value))}
            >
              {spdChapters.map((sp) => (
                <MenuItem key={sp.id} value={sp.id}>
                  {spdLabel(sp)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <DatePicker
            label="Start date"
            value={startDate}
            onChange={(v) => setStartDate(v)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <DatePicker
            label="End date (optional)"
            value={endDate}
            onChange={(v) => setEndDate(v)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Save />}
            disabled={isSaving || !refRoleId || !chapterValid || !startDate}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button size="small" startIcon={<Cancel />} onClick={onCancel}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function FundRoleSlotDisplay({
  title = 'Role-based assignments',
  slots,
  groupRoleOptions,
  refRoleOptions,
  spdChapters,
  canEdit,
  isSaving,
  fixedGroupRoleId,
  emptyMessage = 'No roles assigned to fund',
  onCreate,
  onPatch,
  onDelete,
}: FundRoleSlotDisplayProps) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { activeSlots, historicalSlots } = useMemo(() => {
    const active = slots.filter((s) => s.is_active);
    const historical = slots.filter((s) => !s.is_active);
    return { activeSlots: active, historicalSlots: historical };
  }, [slots]);

  const renderSlot = (slot: FundRoleSlotDTO) => {
    if (editingId === slot.id) {
      return (
        <SlotEditor
          key={slot.id}
          slot={slot}
          groupRoleOptions={groupRoleOptions}
          refRoleOptions={refRoleOptions}
          spdChapters={spdChapters}
          fixedGroupRoleId={fixedGroupRoleId}
          isSaving={isSaving}
          onSave={async (body) => {
            try {
              await onPatch(slot.id, body as PatchFundRoleSlotRequest);
              setEditingId(null);
            } catch {
              // error surfaced by parent
            }
          }}
          onCancel={() => setEditingId(null)}
        />
      );
    }
    return (
      <Box
        key={slot.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="body2">{slot.display_label}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(slot.start_date)}
            {slot.end_date ? ` – ${formatDate(slot.end_date)}` : ' – present'}
            {slot.group_role_name ? ` · ${slot.group_role_name}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={slot.is_active ? 'Active' : 'Past'}
            color={slot.is_active ? 'success' : 'default'}
            variant="outlined"
          />
          {canEdit && (
            <>
              <IconButton
                size="small"
                aria-label="Edit role slot"
                onClick={() => setEditingId(slot.id)}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Remove role slot"
                onClick={() => {
                  if (window.confirm('Remove this role assignment?')) {
                    onDelete(slot.id);
                  }
                }}
              >
                <Cancel fontSize="small" />
              </IconButton>
            </>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      {adding && (
        <SlotEditor
          groupRoleOptions={groupRoleOptions}
          refRoleOptions={refRoleOptions}
          spdChapters={spdChapters}
          fixedGroupRoleId={fixedGroupRoleId}
          isSaving={isSaving}
          onSave={async (body) => {
            try {
              await onCreate(body as CreateFundRoleSlotRequest);
              setAdding(false);
            } catch {
              // error surfaced by parent
            }
          }}
          onCancel={() => setAdding(false)}
        />
      )}
      {activeSlots.length === 0 && historicalSlots.length === 0 && !adding && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {emptyMessage}
        </Typography>
      )}
      {activeSlots.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary">
            Active
          </Typography>
          {activeSlots.map(renderSlot)}
        </>
      )}
      {historicalSlots.length > 0 && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            Historical
          </Typography>
          {historicalSlots.map(renderSlot)}
        </>
      )}
      {canEdit && !adding && (
        <Button size="small" sx={{ mt: 1 }} onClick={() => setAdding(true)}>
          Add role assignment
        </Button>
      )}
    </Box>
  );
}
